/**
 * DEPRECATED: This file is kept for backward compatibility only.
 * New code should use API calls and stores instead of IndexedDB.
 * 
 * Migration Status: IN PROGRESS
 * - Project and Photo data now stored on server
 * - Types exported for backward compatibility
 * - IndexedDB implementation removed
 */

import type { Coordinate, ElementType, Inspection } from '../types';
import type { PhotoAnnotation } from '../types/anomaly';

// ============================================================================
// TYPES ONLY (for backward compatibility)
// ============================================================================

export type ProjectStatus = 'draft' | 'in_progress' | 'review' | 'completed' | 'exported';

export interface ProjectSettings {
  defaultCamera: 'FRONTE' | 'RETRO' | 'ALL';
  autoSave: boolean;
  language: 'it' | 'en';
}

export interface BoundingBox {
  north: number;
  south: number;
  east: number;
  west: number;
}

export interface DBProject {
  id: string;
  code: string;
  name: string;
  line: string;
  status: ProjectStatus;
  progress: number;
  photoCount: number;
  anomalyCount: number;
  elementCount: number;
  createdAt: Date;
  updatedAt: Date;
  surveyDate?: Date;
  exportedAt?: Date;
  epsg: number;
  boundingBox?: BoundingBox;
  thumbnail?: string;
  settings: ProjectSettings;
  inspectionData?: Inspection;
}

export interface DBPhoto {
  id: string;
  projectId: string;
  filename: string;
  camera: 'FRONTE' | 'RETRO' | 'UNKNOWN';
  dateTaken?: Date;
  createdAt: Date;
  latitude?: number;
  longitude?: number;
  altitude?: number;
  elementId?: number;
  elementType?: ElementType;
  blob?: Blob;
  thumbnail?: string;
  width?: number;
  height?: number;
}

export interface DBTrajectoryPoint {
  id: string;
  projectId: string;
  index: number;
  timestamp?: number;
  latitude: number;
  longitude: number;
  altitude?: number;
  roll?: number;
  pitch?: number;
  yaw?: number;
}

export interface DBElement {
  id: string;
  projectId: string;
  elementId: number;
  type: ElementType;
  code: string;
  description?: string;
  latitude?: number;
  longitude?: number;
  coordinates?: Coordinate[];
  properties: Record<string, unknown>;
}

export interface DBAnomaly {
  id: string;
  projectId: string;
  photoId: string;
  photoFileName: string;
  elementId: number;
  elementType: ElementType;
  elementCode: string;
  detailElementId?: string;
  detailElementType?: 'SUPPORT';
  code: string;
  description: string;
  level: 1 | 2 | 3;
  note?: string;
  pixelX: number;
  pixelY: number;
  annotations: PhotoAnnotation[];
  latitude?: number;
  longitude?: number;
  createdAt: Date;
  updatedAt: Date;
}

export type ActivityType = 
  | 'project_created'
  | 'project_updated'
  | 'project_exported'
  | 'photo_uploaded'
  | 'anomaly_added'
  | 'anomaly_updated'
  | 'anomaly_deleted';

export interface DBActivity {
  id: string;
  type: ActivityType;
  projectId: string;
  projectName: string;
  description: string;
  timestamp: Date;
  metadata?: Record<string, unknown>;
}

export interface DBSettings {
  id: string;
  theme: 'light' | 'dark' | 'system';
  language: 'it' | 'en';
  sidebarCollapsed: boolean;
  defaultView: 'table' | 'grid';
  recentProjectIds: string[];
}

// ============================================================================
// DEPRECATED FUNCTIONS (stubs for backward compatibility)
// ============================================================================

export const db = {
  projects: {
    toArray: async () => {
      console.warn('DEPRECATED: db.projects.toArray() - Use API instead');
      return [];
    },
    get: async (id: string) => {
      console.warn('DEPRECATED: db.projects.get() - Use API instead');
      return null;
    },
    put: async (project: DBProject) => {
      console.warn('DEPRECATED: db.projects.put() - Use API instead');
      return project.id;
    },
    delete: async (id: string) => {
      console.warn('DEPRECATED: db.projects.delete() - Use API instead');
    },
  },
  photos: {
    where: (key: string) => ({
      equals: (value: any) => ({
        toArray: async () => {
          console.warn('DEPRECATED: db.photos - Use API instead');
          return [];
        },
      }),
    }),
  },
  anomalies: {
    where: (key: string) => ({
      equals: (value: any) => ({
        toArray: async () => {
          console.warn('DEPRECATED: db.anomalies - Use API instead');
          return [];
        },
      }),
    }),
  },
  activities: {
    toArray: async () => {
      console.warn('DEPRECATED: db.activities - Use API instead');
      return [];
    },
  },
};

export function generateId(): string {
  return crypto.randomUUID();
}

export function createDefaultProject(partial: Partial<DBProject>): DBProject {
  console.warn('DEPRECATED: createDefaultProject() - Use API to create projects');
  const now = new Date();
  return {
    id: generateId(),
    code: '',
    name: '',
    line: '',
    status: 'draft',
    progress: 0,
    photoCount: 0,
    anomalyCount: 0,
    elementCount: 0,
    createdAt: now,
    updatedAt: now,
    epsg: 32632,
    settings: {
      defaultCamera: 'FRONTE',
      autoSave: true,
      language: 'it',
    },
    ...partial,
  };
}

export function getDefaultSettings(): DBSettings {
  return {
    id: 'user_settings',
    theme: 'light',
    language: 'it',
    sidebarCollapsed: false,
    defaultView: 'grid',
    recentProjectIds: [],
  };
}

export async function getUserSettings(): Promise<DBSettings> {
  console.warn('DEPRECATED: getUserSettings() - Move to user preferences API');
  const stored = localStorage.getItem('user_settings');
  if (stored) {
    try {
      return JSON.parse(stored);
    } catch {
      return getDefaultSettings();
    }
  }
  return getDefaultSettings();
}

export async function updateUserSettings(updates: Partial<DBSettings>): Promise<void> {
  console.warn('DEPRECATED: updateUserSettings() - Move to user preferences API');
  const current = await getUserSettings();
  const updated = { ...current, ...updates };
  localStorage.setItem('user_settings', JSON.stringify(updated));
}

export async function updateProjectCounts(projectId: string): Promise<void> {
  console.warn('DEPRECATED: updateProjectCounts() - Server handles this now');
}

export async function logActivity(
  type: ActivityType,
  projectId: string,
  projectName: string,
  description: string,
  metadata?: Record<string, unknown>
): Promise<void> {
  console.warn('DEPRECATED: logActivity() - Use API for activity logging');
}

export async function deleteProject(projectId: string): Promise<void> {
  console.warn('DEPRECATED: deleteProject() - Use projectsApi.delete() instead');
}

export async function addToRecentProjects(projectId: string): Promise<void> {
  console.warn('DEPRECATED: addToRecentProjects() - Use projectStore instead');
}
